import { Link } from 'wouter';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MapPin, Users, Music, Theater, Building2, Landmark, Trophy, Heart, Mic2, Building } from 'lucide-react';
import { VenueWithShows } from '@shared/schema';
import { useAuth } from '@/lib/auth';
import { useState } from 'react';
import { BrowseFilters } from '@/modules/browse/types/browse.types';
import { filterShowsByAudienceMatch } from '@/lib/audience-matching';
import { trimShowsByFilters, type ShowFilterConfig } from '@shared/filter-engine';

interface VenueCardProps {
  venue: VenueWithShows;
  isShowSaved?: (showId: number) => boolean;
  onUnsaveAllShows?: (venue: VenueWithShows) => void;
  onSaveVenue?: (venue: VenueWithShows) => void;
  isVenueSaved?: (venueId: number) => boolean;
  currentFilters?: BrowseFilters;
}

export function VenueCard({ venue, isShowSaved, onUnsaveAllShows, onSaveVenue, isVenueSaved, currentFilters }: VenueCardProps) {
  const { user } = useAuth();
  const [imageError, setImageError] = useState(false);

  const formatCapacity = (capacity: number | null) => {
    if (!capacity) return 'N/A';
    if (capacity < 1000) return `${capacity}`;
    if (capacity < 1000000) return `${(capacity / 1000).toFixed(1)}K`;
    return `${(capacity / 1000000).toFixed(1)}M`;
  };

  const getVenueType = (capacity: number | null) => {
    if (!capacity) return 'Unknown';
    if (capacity < 500) return 'Intimate';
    if (capacity < 2000) return 'Club';
    if (capacity < 5000) return 'Theater';
    if (capacity < 20000) return 'Arena';
    return 'Stadium';
  };

  const getVenueTypeIcon = (capacity: number | null) => {
    if (!capacity) return Building;
    if (capacity < 500) return Music;
    if (capacity < 2000) return Building2;
    if (capacity < 5000) return Theater;
    if (capacity < 20000) return Landmark;
    return Trophy;
  };


  // Filter shows based on current filters using shared functions
  const upcomingShows = (() => {
    const showConfig: ShowFilterConfig = currentFilters ? {
      dateFrom: currentFilters.dateFrom,
      dateTo: currentFilters.dateTo,
      genre: currentFilters.genre,
      location: currentFilters.location,
      artistName: currentFilters.artistName,
      venueName: currentFilters.venueName,
    } : {};
    let filtered = trimShowsByFilters(venue.shows, showConfig);
    if (currentFilters) {
      filtered = filterShowsByAudienceMatch(filtered, currentFilters.targetAudience, currentFilters.minMatchPercentage);
    }
    return filtered;
  })();

  const defaultImage = "https://max.live/hubfs/Concert%20-%20lights%20-%20phone%20-%20flashlight%20-%20artist%20on%20stage.jpg";
  const getImageSrc = () => {
    return venue.imageUrl || defaultImage;
  };

  const VenueTypeIcon = getVenueTypeIcon(venue.capacity);

  return (
    <Link href={`/venue/${venue.id}`} onClick={(e) => {
        // Preserve filters in URL when navigating to venue detail
        e.preventDefault();
        const params = new URLSearchParams(window.location.search);
        const queryString = params.toString();
        window.location.href = `/venue/${venue.id}${queryString ? `?${queryString}` : ''}`;
      }}>
      <div className="relative aspect-square rounded-lg overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 ease-out cursor-pointer touch-manipulation active:scale-[0.98] group will-change-transform" style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}>
        {/* Full-bleed venue image */}
        <img
          src={getImageSrc()}
          alt={`${venue.name} venue`}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={(e) => {
            (e.target as HTMLImageElement).src = defaultImage;
            setImageError(true);
          }}
        />

        {/* Save heart button - top right */}
        {user && onSaveVenue && isVenueSaved && (
          <button
            className={`absolute top-4 right-4 p-2 rounded-full backdrop-blur-md border transition-all duration-200 hover:scale-110 active:scale-95 z-20 group/heart ${
              isVenueSaved(venue.id)
                ? 'bg-[#FA017C]/20 border-[#FA017C]/40 hover:bg-[#FA017C]/30'
                : 'bg-black/20 border-white/20 hover:bg-[#FA017C]/20'
            }`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSaveVenue(venue);
            }}
            aria-label={isVenueSaved(venue.id) ? `Remove ${venue.name} from saved venues` : `Save ${venue.name} to saved venues`}
          >
            <Heart
              className={`w-4 h-4 transition-all duration-200 ${
                isVenueSaved(venue.id)
                  ? "fill-[#FA017C] text-[#FA017C]"
                  : "text-white/80 group-hover/heart:text-[#FA017C] group-hover/heart:fill-[#FA017C]"
              }`}
            />
          </button>
        )}

        {/* Dark glassmorphism overlay for content - bottom third */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-md border-t border-white/10 p-3 space-y-2">
          {/* Venue name with show count */}
          <div className="flex items-center justify-between gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <h3 className="text-lg font-bold text-white truncate flex-1 min-w-0">
                    {venue.name}
                  </h3>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>{venue.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="flex items-center gap-1 text-white/90 flex-shrink-0">
              <Mic2 className="w-4 h-4 text-white/70" />
              <span className="text-base font-semibold">{upcomingShows.length}</span>
            </div>
          </div>

          {/* Metadata with icons and View Shows button */}
          <div className="flex items-end justify-between gap-3">
            <div className="space-y-1 flex-1 min-w-0 overflow-hidden">
              {/* Location */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 text-xs text-white/90 min-w-0">
                      <MapPin className="w-3 h-3 text-white/70 flex-shrink-0" />
                      <span className="truncate">{venue.city}, {venue.state}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p>{venue.city}, {venue.state}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Venue Type & Capacity */}
              <div className="flex items-center gap-1.5 text-xs text-white/90 min-w-0">
                <VenueTypeIcon className="w-3 h-3 text-white/70 flex-shrink-0" />
                <span className="truncate">{venue.venueType || getVenueType(venue.capacity)}</span>
                <span className="text-white/60">•</span>
                <Users className="w-3 h-3 text-white/70 flex-shrink-0" />
                <span className="truncate">{formatCapacity(venue.capacity)}</span>
              </div>
            </div>

            {/* View Shows CTA Button */}
            <div className="flex-shrink-0">
              <div className="px-3 py-1.5 border border-[#FA017C] bg-white/10 backdrop-blur-sm text-white hover:bg-[#FA017C]/20 hover:border-[#FA017C] transition-all duration-200 text-xs rounded-md font-medium whitespace-nowrap">
                View Shows →
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
